import { ANALYTICS_QUERY } from "@pages/analytics/constants";
import { useAnalyticsQuery } from "@pages/analytics/hooks";
import { useAnalyticsStoreShallow } from "@pages/analytics/store";
import { VersionType } from "@pages/analytics/types";
import { IconFile } from "@tabler/icons-react";
import moment from "moment";
import { useCallback, useMemo } from "react";

const dates = {
  from: moment().subtract(12, "month").format("YYYY-MM-DD"),
  to: moment().add(1, "days").format("YYYY-MM-DD"),
};

export const VersionsGroup = ({
  searchTerm,
  onClose,
}: {
  searchTerm: string;
  onClose: () => void;
}) => {
  const { setMultipleFilters } = useAnalyticsStoreShallow((s) => ({
    setMultipleFilters: s.setMultipleFilters,
  }));
  const { data: response, isLoading } = useAnalyticsQuery({
    query: [ANALYTICS_QUERY.VERSIONS],
    skipDebounce: true,
    enabled: searchTerm.length >= 3,
    overrides: {
      location: "",
      storyId: "",
      versionId: "",
      versionTitle: "",
      userId: "",
      dateFrom: dates.from,
      dateTo: dates.to,
      showUnread: false,
      folderId: "",
    },
  });
  const versions = useMemo(() => {
    const versionsData = response?.[ANALYTICS_QUERY.VERSIONS] as
      | VersionType[]
      | undefined;
    return versionsData || [];
  }, [response]);

  const filteredVersions = useMemo(
    () => filterVersions(versions, searchTerm),
    [versions, searchTerm],
  );

  const handleVersionChange = useCallback(
    (version: VersionType) => {
      setMultipleFilters({
        version: { id: version.version_id, title: version.version_title },
        location: "",
        story: { id: "", title: "" },
        user: { id: "", name: "" },
        folder: { id: "", name: "" },
      });
      onClose();
    },
    [setMultipleFilters, onClose],
  );

  return (
    <div className="tw-p-1 tw-border-b tw-border-b-solid tw-border-b-blue-100">
      <span className="tw-py-1 tw-px-2 tw-block tw-text-blue-500 tw-text-xs tw-font-sans">
        Versions
      </span>
      {isLoading ? (
        <VersionsSkeleton />
      ) : filteredVersions.length === 0 ? (
        <div className="tw-py-2 tw-text-center tw-text-sm tw-text-blue-500">
          {searchTerm ? "No versions found" : "No versions available"}
        </div>
      ) : (
        filteredVersions.map((version) => (
          <VersionItem
            key={version.version_id}
            version={version}
            onVersionChange={handleVersionChange}
          />
        ))
      )}
    </div>
  );
};

const VersionItem = ({
  version,
  onVersionChange,
}: {
  version: VersionType;
  onVersionChange: (version: VersionType) => void;
}) => {
  return (
    <button
      type="button"
      className="tw-py-1.5 tw-w-full tw-px-2 tw-outline-none tw-text-blue tw-text-sm tw-transition-colors hover:tw-bg-blue-50 tw-cursor-pointer tw-grid tw-grid-cols-[auto_1fr] tw-gap-2 tw-items-center tw-rounded"
      onClick={() => onVersionChange(version)}
    >
      <IconFile className="tw-shrink-0" />
      <span className="tw-truncate tw-text-left">{version.version_title}</span>
    </button>
  );
};

const SKELETON_WIDTHS = [65, 80, 45, 90, 55];

const VersionsSkeleton = () => {
  return (
    <>
      {SKELETON_WIDTHS.map((width) => (
        <div
          key={width}
          className="tw-flex tw-h-8 tw-w-full tw-items-center tw-justify-between tw-rounded tw-px-2 tw-animate-skeleton-loading"
        >
          <span
            className="tw-block tw-h-2 tw-w-24 tw-rounded-lg tw-bg-blue-100"
            style={{ width: `${width}%` }}
          />
        </div>
      ))}
    </>
  );
};

const filterVersions = (
  versions: VersionType[],
  searchTerm: string,
): VersionType[] => {
  if (!searchTerm.trim()) {
    // Filter out duplicates by version_id when no search term is given
    const uniqueMap = new Map<string, VersionType>();
    for (const v of versions) {
      if (!uniqueMap.has(v.version_id)) {
        uniqueMap.set(v.version_id, v);
      }
    }
    return Array.from(uniqueMap.values());
  }

  const lowerSearchTerm = searchTerm.toLowerCase().trim();

  // Filter, then ensure uniqueness by version_id
  const filtered = versions.filter((version) => {
    const versionTitle = (version.version_title || "").toLowerCase();
    return versionTitle.includes(lowerSearchTerm);
  });

  const uniqueMap = new Map<string, VersionType>();
  for (const v of filtered) {
    if (!uniqueMap.has(v.version_id)) {
      uniqueMap.set(v.version_id, v);
    }
  }
  return Array.from(uniqueMap.values());
};
